/* eslint-disable react/destructuring-assignment */
import React, { useContext } from 'react';
import { styled } from '@abyss/ui/tools/styled';
import { FormControl } from '@abyss/ui/form/FormControl';
import { useForm } from '@abyss/ui/form/hooks/useForm';
import { TextInput } from '@abyss/ui/form/TextInput';
import { ErrorMessage } from '@abyss/ui/form/ErrorMessage';
import { Button } from 'src/common/Button';
import { Flex } from '@abyss/ui/base/Flex';
import { useUserLookup } from 'src/hooks/useUserLookup';
import { AlertContext } from 'src/tools/AlertContext';
import PropTypes from 'prop-types';

const ActionButtons = styled.div({
  marginTop: 25,

  '> *:first-child': {
    marginRight: 12,
  },
});

export const FindForm = ({ modelName }) => {
  const form = useForm();
  const errorContext = useContext(AlertContext);
  const [userLookupResult, getUserLookup] = useUserLookup({
    onCompleted: emulatedUser => {
      form.load(`${modelName}.emulatedUser`, emulatedUser);
      if (emulatedUser.acceptedTerms === false) {
        errorContext.addAlert({
          ...emulatedUser,
          id: 'technical-user-get-user-info',
          code: 'TNC_NOT_ACCEPTED',
          type: 'warning',
        });
      }
    },
    onError: error => {
      errorContext.addAlert({
        message:
          'The One Healthcare ID entered does not exist for emulation. Please try again with a different ID or verify in Secure that your access is still active.',
      });
    },
  });

  const handleFind = () => {
    form.reset(`${modelName}.emulatedUser`);
    form.submit(`${modelName}.ohid`, ohid => {
      let variables = {
        username: ohid,
      };
      if (/^[a-z0-9._]+@(optum|uhc).com$/.test(ohid.toLowerCase())) {
        variables = {
          email: ohid,
        };
      }
      getUserLookup({
        variables,
      });
    });
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleFind();
    }
  };

  const handleReset = () => {
    form.reset(modelName);
  };

  return (
    <Flex gutters-horizontal onKeyDown={handleKeyDown}>
      <Flex.Content space-25>
        <FormControl
          id="ohid-id-input"
          data-cy="ohid-id-input"
          model={`${modelName}.ohid`}
          label="One Healthcare ID"
          required
        >
          {(inputProps, errorProps) => (
            <React.Fragment>
              <TextInput
                {...inputProps}
                autoFocus
                validators={{ required: value => !value }}
              />
              <ErrorMessage
                {...errorProps}
                messages={{ required: 'Please enter an One Healthcare ID.' }}
              />
            </React.Fragment>
          )}
        </FormControl>
      </Flex.Content>
      <Flex.Content>
        <ActionButtons>
          <Button
            data-testid="find-button"
            data-cy="find-button"
            onClick={handleFind}
            isLoading={userLookupResult.isLoading}
          >
            Find
          </Button>
          <Button
            data-testid="reset-button"
            data-cy="reset-button"
            variant="outline"
            onClick={handleReset}
          >
            Reset
          </Button>
        </ActionButtons>
      </Flex.Content>
    </Flex>
  );
};

FindForm.propTypes = {
  modelName: PropTypes.string.isRequired,
};
